/**
 * EducationalTrips — /educational-trips
 *
 * Family educational tourism page combining Himachal sacred-site visits
 * with existing Dhanurveda and Sanskrit Samvad programs.
 * Trip data is YAML-driven (portal_config.yaml educational_trips key).
 */
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/EducationalTrips.css';

interface Props {
  onNavigate: (path: string) => void;
}

const TRIPS = [
  {
    id: 'dharamshala-archery-samvad',
    title: { hi: 'धर्मशाला — धनुर्वेद व संस्कृत संवाद यात्रा', en: 'Dharamshala — Archery & Sanskrit Samvad Journey' },
    duration: { hi: '१५ दिन', en: '15 Days' },
    location: { hi: 'धर्मशाला, हिमाचल प्रदेश', en: 'Dharamshala, Himachal Pradesh' },
    programs: ['धनुर्वेद 🏹', 'संस्कृत संवाद 🗣️'],
    includes: {
      hi: ['काष्ठ-धनुष अभ्यास', 'संस्कृत संवाद सत्र', 'किशोरीलाल धार ट्रेक', 'अघंझर महादेव मठ दर्शन', 'भागसूनाग झरना यात्रा'],
      en: ['Traditional wooden-bow archery', 'Sanskrit conversation sessions', 'Kishorelal Dhar trek', 'Aghanjhar Mahadev Monastery visit', 'Bhagsu Falls excursion'],
    },
    price: { hi: '₹४५,०००/परिवार', en: '₹45,000/family' },
    minAge: 9,
    maxGroupSize: 8,
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1775926720/Gemini_ijd695ijd695ijd6_upscayl_1x_upscayl-standard-4x_oxiptq.png',
    badge: { hi: 'सर्वाधिक लोकप्रिय', en: 'Most Popular' },
  },
  {
    id: 'himachal-sacred-circuit',
    title: { hi: 'हिमाचल पवित्र परिपथ', en: 'Himachal Sacred Circuit' },
    duration: { hi: '७ दिन', en: '7 Days' },
    location: { hi: 'धर्मशाला · मैक्लोडगंज · बिर-बिलिंग', en: 'Dharamshala · McLeodGanj · Bir-Billing' },
    programs: ['पवित्र स्थल 🏔️', 'संस्कृत संवाद 🗣️'],
    includes: {
      hi: ['मसरूर मंदिर दर्शन', 'ज्वालामुखी मंदिर यात्रा', 'संस्कृत संवाद छावनी', 'बाथू की लड़ी', 'स्थानीय हिमाचली भोजन'],
      en: ['Masrur Temple (rock-cut)', 'Jwalamukhi Devi pilgrimage', 'Sanskrit morning dialogue', 'Bir monastery meditation', 'Local Himachali cuisine'],
    },
    price: { hi: '₹२८,०००/परिवार', en: '₹28,000/family' },
    minAge: 6,
    maxGroupSize: 12,
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1779846576/Gemini_Generated_Image_j7up2xj7up2xj7up_upscayl_2x_upscayl-lite-4x_pmvcrc.png',
    badge: null,
  },
  {
    id: 'full-gurukul-immersion',
    title: { hi: 'राजधर्म गुरुकुल पूर्ण अवगाहन', en: 'Raajadharma Gurukul Full Immersion' },
    duration: { hi: '३० दिन', en: '30 Days' },
    location: { hi: 'धर्मशाला, हिमाचल प्रदेश', en: 'Dharamshala, Himachal Pradesh' },
    programs: ['धनुर्वेद 🏹', 'संस्कृत संवाद 🗣️', 'पवित्र स्थल 🏔️'],
    includes: {
      hi: ['सम्पूर्ण धनुर्वेद कोर्स', '१२ संस्कृत संवाद सत्र', 'हिमाचल दर्शन', 'योगसूत्र पाठ', 'वर्ण-आधारित कर्म-परियोजना'],
      en: ['Full Dhanurveda course', '12 Sanskrit Samvad sessions', 'Himachal heritage sites', 'Yoga Sutra recitation', 'Varna-based karma project'],
    },
    price: { hi: '₹९०,०००/परिवार', en: '₹90,000/family' },
    minAge: 9,
    maxGroupSize: 6,
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1779846991/king_upscayl_2x_upscayl-lite-4x_of3yhf.png',
    badge: { hi: 'प्रीमियम', en: 'Premium' },
  },
];

const SAFETY = [
  { icon: '🛡️', hi: 'अनुभवी राजधर्म प्रशिक्षक हर समय उपस्थित', en: 'Experienced Rajadharma instructors present at all times' },
  { icon: '👨‍👩‍👧', hi: 'अभिभावक-वार्ड समूह नीति — बिना अभिभावक के किशोर नहीं', en: 'Parent-ward group policy — no unaccompanied minors' },
  { icon: '🏥', hi: 'प्राथमिक चिकित्सा प्रशिक्षित कर्मचारी एवं स्थानीय चिकित्सा सम्पर्क', en: 'First-aid trained staff and local medical contacts' },
  { icon: '📋', hi: 'स्वास्थ्य प्रपत्र एवं अभिभावक सहमति पूर्व-आवश्यक', en: 'Health forms and guardian consent required before travel' },
  { icon: '🔒', hi: 'सुरक्षित आवास — पूर्व-निरीक्षित एवं अनुमोदित', en: 'Secure accommodation — pre-inspected and approved' },
];

export default function EducationalTrips({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const hi = locale === 'hi';

  const tl = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  return (
    <div className="et-page">
      {/* Hero */}
      <div className="et-hero">
        <span className="brand-section-label">{tl('trips.label', 'Educational Trips')}</span>
        <h1 className="et-hero-title">
          {hi ? 'शैक्षिक यात्राएँ' : 'Sacred Educational Journeys'}
        </h1>
        <p className="et-hero-sub">
          {hi
            ? 'हिमाचल प्रदेश की पवित्र भूमि में राजधर्म के दो ध्वजवाहक — धनुर्वेद और संस्कृत संवाद — का जीवंत अनुभव। पूरे परिवार के साथ।'
            : 'Experience Rajadharma\'s two flagship programs — Dhanurveda and Sanskrit Samvad — in the sacred landscape of Himachal Pradesh. For the whole family.'}
        </p>
        <div className="et-hero-actions">
          <button className="et-btn-primary" onClick={() => onNavigate('/booking')}>
            {hi ? '🏕️ यात्रा बुक करें' : '🏕️ Book a Trip'}
          </button>
          <button className="et-btn-ghost" onClick={() => onNavigate('/sacred-sites')}>
            {hi ? '🏔️ पवित्र स्थल देखें' : '🏔️ View Sacred Sites'}
          </button>
        </div>
      </div>

      {/* Trip cards */}
      <section className="et-section">
        <h2 className="et-section-title">
          {hi ? 'उपलब्ध यात्राएँ' : 'Available Trips'}
        </h2>
        <div className="et-cards">
          {TRIPS.map(trip => (
            <div key={trip.id} className="et-card">
              <div className="et-card-img" style={{ backgroundImage: `url(${trip.image})` }}>
                {trip.badge && (
                  <span className="et-card-badge trust-badge">
                    {hi ? trip.badge.hi : trip.badge.en}
                  </span>
                )}
              </div>
              <div className="et-card-body">
                <h3 className="et-card-title">{hi ? trip.title.hi : trip.title.en}</h3>
                <p className="et-card-meta">
                  📍 {hi ? trip.location.hi : trip.location.en} · ⏱ {hi ? trip.duration.hi : trip.duration.en}
                </p>
                <div className="et-card-programs">
                  {trip.programs.map(p => (
                    <span key={p} className="et-prog-tag">{p}</span>
                  ))}
                </div>
                <ul className="et-card-includes">
                  {(hi ? trip.includes.hi : trip.includes.en).map(item => (
                    <li key={item}>✔ {item}</li>
                  ))}
                </ul>
                <div className="et-card-footer">
                  <span className="et-price">{hi ? trip.price.hi : trip.price.en}</span>
                  <span className="et-age-note">
                    {hi ? `न्यूनतम आयु ${trip.minAge} वर्ष` : `Min. age ${trip.minAge} yrs`}
                  </span>
                </div>
                <button
                  className="et-card-cta"
                  onClick={() => onNavigate(`/booking?trip=${trip.id}`)}
                >
                  {hi ? 'इस यात्रा के लिए बुक करें →' : 'Book this trip →'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Safety protocols */}
      <section className="et-section et-safety-section">
        <h2 className="et-section-title">
          🛡️ {hi ? 'सुरक्षा एवं विश्वसनीयता प्रोटोकॉल' : 'Safety & Reliability Protocols'}
        </h2>
        <div className="et-safety-grid">
          {SAFETY.map(item => (
            <div key={item.en} className="et-safety-item">
              <span className="et-safety-icon">{item.icon}</span>
              <span>{hi ? item.hi : item.en}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Existing programs callout */}
      <section className="et-section et-programs-callout">
        <p className="et-callout-text">
          {hi
            ? 'ये यात्राएँ राजधर्म के मौजूदा कार्यक्रमों — धनुर्वेद और संस्कृत संवाद — को परिवार-अनुकूल शैक्षिक पर्यटन अनुभव में बुनती हैं।'
            : 'These trips weave Rajadharma\'s existing programs — Dhanurveda and Sanskrit Samvad — into a family-oriented educational tourism experience.'}
        </p>
        <div className="et-callout-actions">
          <button className="et-btn-ghost" onClick={() => onNavigate('/rajdharma/dhanurveda')}>
            {hi ? '🏹 धनुर्वेद जानें' : '🏹 Learn Dhanurveda'}
          </button>
          <button className="et-btn-ghost" onClick={() => onNavigate('/samvad')}>
            {hi ? '🗣️ संस्कृत संवाद जानें' : '🗣️ Learn Sanskrit Samvad'}
          </button>
        </div>
      </section>
    </div>
  );
}
